/**
 * As regras da aba Coloração, sem React e sem banco: como a linha lida vira o formulário e como o
 * formulário vira o registro gravado. Idênticas ao `carregarColoracao` e ao clique de salvar do
 * legado (`admin.html:5273-5337`).
 *
 * Moram fora da tela para a suíte e quem ler poderem conferir cada decisão contra o legado sem
 * desenhar nada, e para a tela só ligar estado a campo.
 */

// A linha da tabela `checklist_coloracao`, como o pacote de dados a tipa.
import type { LinhaChecklistColoracao } from '@studiolu/dados';
// Os catálogos fixos das etapas, cópia única da ficha (A2).
import {
  ESTACOES_12,
  ETAPAS_COLORACAO_CHECKBOX,
  FAMILIAS_CORES_COLORACAO,
  OPCOES_ESTACAO_SIMPLES,
} from '../../constantes.js';
// O `includes` do legado, que aceita lista ou texto (A2).
import { contem } from '../../comum-ficha/regras.js';

/** Uma linha da Etapa 7 na tela. `chave` só existe para o React não confundir linhas ao remover. */
export interface LinhaDeComparativo {
  readonly chave: number;
  readonly campo1: string;
  readonly campo2: string;
  readonly resultado: string;
}

/** O estado do formulário inteiro da aba, campo a campo como no legado. */
export interface FormularioColoracao {
  // As marcadas de cada grupo (etapas 1 a 5 e as cinco famílias da etapa 8), pela coluna.
  readonly marcadas: Readonly<Record<string, readonly string[]>>;
  readonly caracteristica_contraste: string;
  readonly caracteristica_intensidade: string;
  readonly caracteristica_temperatura: string;
  readonly caracteristica_profundidade: string;
  readonly comparativos: readonly LinhaDeComparativo[];
  readonly estacao_definida: string;
  readonly resultado_final: string;
}

/** As quatro características da Etapa 6, na ordem da tela (`admin.html:2655-2672`). */
export const CARACTERISTICAS = [
  { campo: 'caracteristica_contraste', rotulo: 'Etapa 6 | Contraste' },
  { campo: 'caracteristica_intensidade', rotulo: 'Intensidade' },
  { campo: 'caracteristica_temperatura', rotulo: 'Temperatura' },
  { campo: 'caracteristica_profundidade', rotulo: 'Profundidade' },
] as const;

/** O nome de uma das quatro colunas de característica. */
export type CampoDeCaracteristica = (typeof CARACTERISTICAS)[number]['campo'];

/**
 * Todos os grupos de marcar da aba, cada um com a coluna e as opções na ordem da tela: as cinco
 * etapas (`admin.html:5192-5199`) e as cinco famílias de cor com as quatro estações (`5202-5209`).
 */
export const GRUPOS_DE_MARCAR: readonly { readonly campo: string; readonly opcoes: readonly string[] }[] = [
  ...ETAPAS_COLORACAO_CHECKBOX.map((e) => ({ campo: e.campo, opcoes: e.opcoes })),
  ...FAMILIAS_CORES_COLORACAO.map((f) => ({ campo: f.campo, opcoes: OPCOES_ESTACAO_SIMPLES })),
];

/**
 * O valor que o campo da Etapa 7 MOSTRA depois de ler o banco.
 *
 * DEFEITO DO LEGADO REPRODUZIDO, por decisão do fundador: o legado monta a linha com
 * `value="${dados?.campo1 || ''}"` DENTRO de um `innerHTML` (`admin.html:5221-5226`), sem escapar
 * nada, e o parser de HTML do navegador lê esse atributo. Duas consequências que a pessoa vê, e as
 * duas são reproduzidas aqui:
 *   1. o atributo termina na primeira aspa dupla: `Frio "azulado"` aparece como `Frio `;
 *   2. referência de caractere é decodificada: `a &copy b` aparece como `a © b`, e `R&amp;B` como
 *      `R&B` (medido no Chrome pela verificação independente da A7, com o template exato do legado).
 * Salvar em seguida grava o que o campo mostra, cortado e decodificado, como o `.value` do legado.
 *
 * COMO SE REPRODUZ SEM REPRODUZIR A INJEÇÃO: o texto é cortado na aspa ANTES de ir ao parser, e o
 * que sobra vai para dentro de um `value="..."` de um documento inerte (`DOMParser`, que não roda
 * script nem carrega nada). Sem aspa dupla, nada consegue sair do atributo, e o parser só faz com o
 * texto o que ele faz com o valor de atributo: decodificar as referências pelas regras do próprio
 * navegador (inclusive as que dispensam o ponto e vírgula), que nenhuma tabela escrita à mão iguala.
 * O que vem DEPOIS da aspa, que no legado vira atributo solto no `input` (injeção), não entra: é a
 * exceção de segurança aceita pelo fundador.
 */
export function valorComoOLegadoInjeta(valor: unknown): string {
  // `valor || ''` e a conversão para texto do template do legado.
  const texto = String(valor || '');
  // O atributo termina na primeira aspa dupla; o resto é a parte que seria injeção.
  const aspa = texto.indexOf('"');
  const dentroDoAtributo = aspa === -1 ? texto : texto.slice(0, aspa);
  // O mesmo parser que leu o `innerHTML` do legado, num documento que não executa nada.
  const documento = new DOMParser().parseFromString(`<input value="${dentroDoAtributo}">`, 'text/html');
  const atributo = documento.querySelector('input')?.getAttribute('value') ?? '';
  // O `input type="text"` tira as quebras de linha do valor ao recebê-lo (a limpeza de valor do
  // HTML), então o legado mostra e grava sem elas.
  return atributo.replace(/[\r\n]/g, '');
}

/**
 * A leitura da ficha QUEBRA no meio, como no legado?
 *
 * DEFEITO DO LEGADO REPRODUZIDO, por decisão do fundador: `montarComparativosColoracao` recebe
 * `data?.comparativo_estacoes || []` e faz `(lista && lista.length) ? lista : [null]` seguido de
 * `itens.forEach(...)` (`admin.html:5235-5237`, `5296`). Um valor gravado que NÃO é lista mas tem
 * `length` (um texto não vazio, ou um objeto com `length`) chega ao `forEach`, que não existe nele:
 * o `carregarColoracao` para ali com `TypeError`. Achado da verificação independente da A7.
 *
 * O que a parada deixa na aba, e o `lerLinhaComoOLegado` reproduz: as etapas 1 a 5 e 8 e as quatro
 * características já foram preenchidas; a lista de comparativos já foi esvaziada (`5235`), e fica
 * sem nenhuma linha; a estação e o resultado final ficam com o que estava na tela; o status fica
 * vazio (limpo antes de ler, `5275`, e o "Última atualização em ..." não chega a ser escrito); e o
 * "Salvar" não destrava (`5303` não roda), então não há como gravar por cima do valor quebrado.
 */
export function leituraQuebraNoLegado(linha: LinhaChecklistColoracao | null): boolean {
  // `data?.comparativo_estacoes || []`: ausente, nulo e vazio viram a lista vazia.
  const lista: unknown = linha?.comparativo_estacoes || [];
  // `lista.length` como o legado lê: número e booleano não têm, e dão `undefined`.
  const temTamanho = !!(lista as { readonly length?: unknown }).length;
  // Entre os valores que um `jsonb` devolve, só a lista tem `forEach`.
  return temTamanho && !Array.isArray(lista);
}

/**
 * A linha lida vira o formulário, como o `carregarColoracao` (`admin.html:5273-5305`), na ordem em
 * que o legado preenche a tela: marcas, características, comparativos, estação e resultado.
 *
 * `anterior` é o que está na tela antes da leitura: só a leitura que quebra no meio o usa (ver
 * `leituraQuebraNoLegado`). `novaChave` dá a chave de cada linha da Etapa 7; é parâmetro para a
 * tela manter o contador dela.
 */
export function lerLinhaComoOLegado(
  linha: LinhaChecklistColoracao | null,
  anterior: FormularioColoracao,
  novaChave: () => number,
): FormularioColoracao {
  // A linha lida por nome de coluna, como o `data?.[etapa.campo]` do legado.
  const porColuna = (linha ?? {}) as Readonly<Record<string, unknown>>;
  // Cada caixa aparece marcada se o valor gravado a contém (`valores.includes(cb.value)`).
  const marcadas: Record<string, readonly string[]> = {};
  for (const grupo of GRUPOS_DE_MARCAR) {
    marcadas[grupo.campo] = grupo.opcoes.filter((opcao) =>
      contem(porColuna[grupo.campo] as readonly string[] | string | null | undefined, opcao),
    );
  }
  // As quatro características, preenchidas antes dos comparativos (`5292-5295`).
  const caracteristicas = {
    caracteristica_contraste: linha?.caracteristica_contraste || '',
    caracteristica_intensidade: linha?.caracteristica_intensidade || '',
    caracteristica_temperatura: linha?.caracteristica_temperatura || '',
    caracteristica_profundidade: linha?.caracteristica_profundidade || '',
  };
  // A leitura quebra no `forEach` dos comparativos: o que vem antes já foi preenchido, a lista já
  // foi esvaziada, e o que vem depois fica como estava (ver `leituraQuebraNoLegado`).
  if (leituraQuebraNoLegado(linha)) return { ...anterior, marcadas, ...caracteristicas, comparativos: [] };
  // `data?.comparativo_estacoes || []`, e lista vazia abre com UMA linha em branco (`5236`).
  const lidos = linha?.comparativo_estacoes;
  const itens: readonly unknown[] = Array.isArray(lidos) && lidos.length > 0 ? lidos : [null];
  return {
    marcadas,
    ...caracteristicas,
    comparativos: itens.map((item) => {
      // `dados?.campo1` do legado: item que não é objeto (texto, número) não tem os campos.
      const campos = (item ?? {}) as Readonly<Record<string, unknown>>;
      return {
        chave: novaChave(),
        campo1: valorComoOLegadoInjeta(campos['campo1']),
        campo2: valorComoOLegadoInjeta(campos['campo2']),
        resultado: valorComoOLegadoInjeta(campos['resultado']),
      };
    }),
    // A estação como veio do banco, MESMO fora das 12: o `select` do legado recebe o valor, não o
    // acha entre as opções e fica sem nenhuma escolhida, com a caixa EM BRANCO (`selectedIndex -1`,
    // `5297`). A tela desenha esse branco, e o `registroDoFormulario` grava o vazio que o
    // `select.value` do legado daria (`5329`).
    estacao_definida: String(linha?.estacao_definida || ''),
    resultado_final: linha?.resultado_final || '',
  };
}

/** O formulário antes de qualquer leitura: a aba vazia, com UMA linha de comparativo em branco. */
export function formularioVazio(novaChave: () => number): FormularioColoracao {
  // Sem linha, nada do `anterior` é lido; ele só precisa existir para o tipo.
  const nada: FormularioColoracao = {
    marcadas: {},
    caracteristica_contraste: '',
    caracteristica_intensidade: '',
    caracteristica_temperatura: '',
    caracteristica_profundidade: '',
    comparativos: [],
    estacao_definida: '',
    resultado_final: '',
  };
  return lerLinhaComoOLegado(null, nada, novaChave);
}

/**
 * O formulário vira o registro do `upsert`, com as colunas NA ORDEM do legado (`admin.html:5308-5331`):
 * etapas 1 a 5, famílias de cor, as quatro características, a Etapa 7, a estação, o resultado e
 * `atualizado_em`. O `user_id` e o `lead_id` entram na frente pelo `registroComLead` do pacote.
 */
export function registroDoFormulario(formulario: FormularioColoracao, agoraIso: string): Record<string, unknown> {
  const registro: Record<string, unknown> = {};
  // Só as opções da tela, na ordem da tela, como o `querySelectorAll(':checked')` do legado.
  for (const grupo of GRUPOS_DE_MARCAR) {
    const marcadas = formulario.marcadas[grupo.campo] ?? [];
    registro[grupo.campo] = grupo.opcoes.filter((opcao) => marcadas.includes(opcao));
  }
  // As quatro características como estão no campo, vazias inclusive.
  for (const { campo } of CARACTERISTICAS) registro[campo] = formulario[campo];
  // A Etapa 7: cada linha vira `{campo1, campo2, resultado}`, e linha toda vazia não é gravada.
  registro['comparativo_estacoes'] = formulario.comparativos
    .map(({ campo1, campo2, resultado }) => ({ campo1, campo2, resultado }))
    .filter((item) => item.campo1 || item.campo2 || item.resultado);
  // Estação fora das 12 é o `select` sem opção escolhida, e o `.value` dele é vazio (`5329`).
  registro['estacao_definida'] = ESTACOES_12.includes(formulario.estacao_definida) ? formulario.estacao_definida : '';
  registro['resultado_final'] = formulario.resultado_final;
  // O instante do clique, como o `new Date().toISOString()` do legado.
  registro['atualizado_em'] = agoraIso;
  return registro;
}
